import React, { useState, useEffect } from 'react';
import CustomButton from '../ui/Button';
import { useTranslation } from "react-i18next";
import { submitContactForm } from '../api/ContactFormApi';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ContactSection = () => {
    const { t } = useTranslation("contact");

    // Form state
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        purpose: '',
        message: ''
    });

    // Validation and submission state
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [isFormValid, setIsFormValid] = useState(false);
    const [submitStatus, setSubmitStatus] = useState({ success: null, message: '' });

    // Regex definitions
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[\d\s-]{10,}$/;

    // Validate a single field based on its name and value
    const validateField = (name, value) => {
        switch (name) {
            case 'first_name':
                return value.trim() ? '' : t("first_name_required");
            case 'last_name':
                return value.trim() ? '' : t("last_name_required");
            case 'email':
                return !value.trim()
                    ? t("email_required")
                    : !emailRegex.test(value)
                        ? t("invalid_email_format")
                        : '';
            case 'phone_number':
                return !value.trim()
                    ? t("phone_number_required")
                    : !phoneRegex.test(value)
                        ? t("invalid_phone_number_format")
                        : '';
            case 'purpose':
                return value ? '' : t("purpose_required");
            case 'message':
                return value.trim() ? '' : t("message_required");
            default:
                return '';
        }
    };

    // Update form data and validate the field
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrors((prev) => ({
            ...prev,
            [name]: validateField(name, value),
        }));
    };

    // Validate the overall form when form data or errors change
    useEffect(() => {
        const hasErrors = Object.values(errors).some((error) => error !== '');
        const allFieldsFilled = Object.values(formData).every((value) => value.trim() !== '');
        setIsFormValid(!hasErrors && allFieldsFilled);
    }, [formData, errors]);

    // Handle form submission and call the POST API


    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isFormValid) {
            setLoading(true); // Start loader
            try {
                const response = await submitContactForm(formData);

                toast.success(t("success"));

                // Reset form
                setFormData({
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone_number: '',
                    purpose: '',
                    message: ''
                });


            } catch (error) {
                console.error("Error submitting form:", error);
                toast.success(t("error"));
            }
            setLoading(false); // Stop loader
        }
    };
    return (
        <div className="relative py-10">
            {/* Background Image */}
            <img
                src="/assets/servicesbg.svg"
                className="w-full h-full object-cover absolute inset-0"
                alt="background animation"
            />
            <div className="absolute inset-0 bg-[#00000099]"></div>
            <img
                src="/assets/ellipse.svg"
                className="absolute bottom-96 left-1/2 transform -translate-x-1/2 translate-y-1/2"
                alt="ellipse decoration"
            />

            <div className="relative z-10 flex justify-center items-center px-4">
                <div className="flex flex-col md:flex-row w-full max-w-7xl rounded-xl shadow-lg p-8 space-y-6 md:space-y-0 md:space-x-6">
                    {/* Left Section: Contact Info */}
                    <div
                        className="
              group 
              w-full 
              md:basis-2/5 
              h-[500px] 
              md:h-[647px] 
              px-6 py-8 
              border border-[#8D8D8D] 
              bg-cover bg-center bg-no-repeat 
              relative overflow-hidden 
              transition-all duration-500 ease-in-out
              contactusbg
              hover:bg-none
            "
                    >
                        {/* Background Overlay */}
                        <div className="absolute inset-0 bg-black/40 rounded-lg group-hover:bg-none transition-all duration-500 "></div>

                        {/* Ellipse Animation */}
                        <img
                            src="/assets/ellipse.svg"
                            alt="Ellipse Decoration"
                            className="
                absolute w-60 h-60 md:w-80 md:h-80 
                bottom-[-100px] md:bottom-[-150px] 
                right-[50px] md:right-[80px] 
                translate-x-1/2 opacity-0 
                group-hover:opacity-100 
                group-hover:bottom-1/4 
                transform scale-50 group-hover:scale-100 
                transition-all duration-700 ease-in-out
              "
                        />

                        {/* Contact Info Content */}
                        <div className="relative z-10">
                            <h2 className="text-xl md:text-2xl text-white mb-4 ml-3">{t("contact_information")}</h2>
                            <p className="text-sm md:text-base text-color-9 ml-3">{t("live_chat_prompt")}</p>

                            {/* Contact Details */}
                            <div className="text-sm md:text-lg text-gray-100 ml-3 mt-16 md:mt-24 space-y-8 md:space-y-14">
                                <div className="flex items-center space-x-3 md:space-x-7">
                                    <img src="/assets/phone.svg" alt="Phone Icon" className="w-5 h-5 md:w-6 md:h-6" />
                                    <a href="tel:+10123456789" className="text-inherit hover:text-[#05897E] no-underline">
                                    +7 701 378 75 27
                                    </a>
                                </div>
                                <div className="flex items-center space-x-3 md:space-x-7">
                                    <img src="/assets/email.svg" alt="Email Icon" className="w-5 h-5 md:w-6 md:h-6" />
                                    <a href="mailto:info@tauken.ai" className="text-inherit hover:text-[#05897E] no-underline">
                                    info@tauken.ai
                                    </a>
                                </div>
                                <div className="flex md:items-start space-x-3 md:space-x-7 max-w-[351px]">
                                    <img src="/assets/map.svg" alt="Address Icon" className="w-5 h-5 md:w-6 md:h-6" />
                                    <p dangerouslySetInnerHTML={{ __html: t("address").replace("Адрес:", "<b>Адрес:</b><br/>") }}></p>

                                </div>
                            </div>
                        </div>

                        {/* Social Media Icons */}
                        <div className="absolute md:right-[-100px] md:right-[-120px] bottom-[-40px] md:bottom-[-60px] left-1/2 transform -translate-x-1/2 flex space-x-4 md:space-x-6 opacity-0 group-hover:opacity-100 group-hover:bottom-6 transition-all duration-700">
                            <a href="https://www.linkedin.com/" target="_blank" rel="noopener noreferrer">
                                <img src="/assets/linkedin.svg" alt="LinkedIn Icon" className="w-8 h-8 md:w-10 md:h-10 hover:scale-110 transition-transform" />
                            </a>
                            <a href="https://twitter.com/login" target="_blank" rel="noopener noreferrer">
                                <img src="/assets/twitter.svg" alt="Twitter Icon" className="w-8 h-8 md:w-10 md:h-10 hover:scale-110 transition-transform" />
                            </a>
                            <a href="https://www.youtube.com/" target="_blank" rel="noopener noreferrer">
                                <img src="/assets/youtube.svg" alt="YouTube Icon" className="w-8 h-8 md:w-10 md:h-10 hover:scale-110 transition-transform" />
                            </a>
                        </div>
                    </div>

                    {/* Right Section: Contact Form */}
                    <form onSubmit={handleSubmit} className="w-full md:basis-3/5 flex flex-col px-4 md:px-6 py-8">
                        {/* Name Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <div>
                                <label htmlFor="first_name" className="block text-sm md:text-base mb-1">{t("first_name")}</label>
                                <input
                                    id="first_name"
                                    name="first_name"
                                    type="text"
                                    value={formData.first_name}
                                    onChange={handleChange}
                                    placeholder="John"
                                    className={`w-full h-10 md:h-12 bg-transparent border-b focus:outline-none ${errors.first_name ? 'border-red-500' : ''
                                        }`}
                                />
                                {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
                            </div>
                            <div>
                                <label htmlFor="last_name" className="block text-sm md:text-base mb-1">{t("last_name")}</label>
                                <input
                                    id="last_name"
                                    name="last_name"
                                    type="text"
                                    value={formData.last_name}
                                    onChange={handleChange}
                                    placeholder="Doe"
                                    className={`w-full h-10 md:h-12 bg-transparent border-b focus:outline-none ${errors.last_name ? 'border-red-500' : ''
                                        }`}
                                />
                                {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
                            </div>
                        </div>

                        {/* Email and phone_number Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <div>
                                <label htmlFor="email" className="block text-sm md:text-base mb-1">{t("email")}</label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="example@example.com"
                                    className={`w-full h-10 md:h-12 bg-transparent border-b focus:outline-none ${errors.email ? 'border-red-500' : ''
                                        }`}
                                />
                                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                            </div>
                            <div>
                                <label htmlFor="phone_number" className="block text-sm md:text-base mb-1">{t("phone_number")}</label>
                                <input
                                    id="phone_number"
                                    name="phone_number"
                                    type="tel"
                                    value={formData.phone_number}
                                    onChange={handleChange}
                                    placeholder="+7 701 000 00 00"
                                    className={`w-full h-10 md:h-12 bg-transparent border-b focus:outline-none ${errors.phone_number ? 'border-red-500' : ''
                                        }`}
                                />
                                {errors.phone_number && <p className="text-red-500 text-xs mt-1">{errors.phone_number}</p>}
                            </div>
                        </div>

                        {/* Purpose Selection */}
                        <div className="mb-6">
                            <h4 className="text-sm md:text-base mb-2">{t("select_purpose")}</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {["general_inquiry", "sales", "support", "feedback"].map((option, index) => (
                                    <label key={index} htmlFor={`purpose${index}`} className="radio-label-container">
                                        <input
                                            type="radio"
                                            id={`purpose${index}`}
                                            name="purpose"
                                            value={option}
                                            onChange={handleChange}
                                            className="radio-input"
                                            checked={formData.purpose === option}
                                        />
                                        <div className="custom-radio">
                                            <img src="/assets/tick.svg" alt="Selected" />
                                        </div>
                                        <span className="title-small-11">{t(option)}</span>
                                    </label>
                                ))}
                            </div>
                            {errors.purpose && <p className="text-red-500 text-xs mt-1">{errors.purpose}</p>}
                        </div>

                        {/* Message Field */}
                        <div className="mb-6">
                            <label htmlFor="message" className="block text-sm md:text-base mb-1">{t("message")}</label>
                            <input
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                placeholder={t("write_your_message")}
                                className={`w-full h-12 bg-transparent border-b focus:outline-none ${errors.message ? 'border-red-500' : ''
                                    }`}
                            />
                            {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                        </div>

                        {/* Submission Status Message */}
                        {submitStatus.message && (
                            <div className={`mb-4 text-sm ${submitStatus.success ? 'text-green-600' : 'text-red-600'}`}>
                                {submitStatus.message}
                            </div>
                        )}

                        {/* Send Button */}
                        <div className="md:ml-auto pt-10">
                            <CustomButton disabled={loading || !isFormValid} type="submit">
                                {loading ? (
                                    <div className="flex items-center space-x-2">
                                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                        <span>{t("submitting")}</span>
                                    </div>
                                ) : (
                                    t("send_message")
                                )}
                            </CustomButton>
                        </div>

                    </form>
                </div>
            </div>
        </div>
    );
};

export default ContactSection;
